import { useCallback, useEffect, useState } from "react";
import { Loader2, Save, Clock, Sunrise, Sunset, Check } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const SLOTS = Array.from({ length: 13 }, (_, i) => `${String(i + 8).padStart(2, "0")}:00`);
const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

const slotLabel = (s) => {
  const h = parseInt(s, 10);
  if (h === 12) return "12 PM";
  return h < 12 ? `${h} AM` : `${h - 12} PM`;
};

const TIMING_OPTIONS = [
  {
    id: "evening_before",
    icon: Sunset,
    title: "Evening before",
    hint: "6:00 PM CT, the day before the job",
  },
  {
    id: "morning_of",
    icon: Sunrise,
    title: "Morning of",
    hint: "7:00 AM CT, the day of the job",
  },
];

export default function AvailabilityTab() {
  const [settings, setSettings] = useState(null);
  const [timing, setTiming] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [avail, rem] = await Promise.all([
        api.get("/admin/availability-settings"),
        api.get("/admin/reminder-settings"),
      ]);
      setSettings(avail.data.weekday_slots);
      setTiming(rem.data.timing);
    } catch (err) {
      toast.error(formatApiError(err, "Couldn't load availability settings."));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = (wd, slot) => {
    setSettings((prev) => {
      const current = prev[wd] || [];
      const next = current.includes(slot)
        ? current.filter((s) => s !== slot)
        : [...current, slot];
      return { ...prev, [wd]: next };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/availability-settings", { weekday_slots: settings });
      toast.success("Availability updated — the calendar reflects it right away.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const saveTiming = async (value) => {
    const previous = timing;
    setTiming(value);
    try {
      await api.put("/admin/reminder-settings", { timing: value });
      toast.success(
        value === "morning_of"
          ? "Reminders now go out at 7 AM the morning of each job."
          : "Reminders now go out at 6 PM the evening before each job."
      );
    } catch (err) {
      setTiming(previous);
      toast.error(formatApiError(err));
    }
  };

  if (!settings) {
    return (
      <div className="flex justify-center py-20 text-pine">
        <Loader2 size={28} className="animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div data-testid="reminder-settings-card" className="bg-white rounded-3xl border border-emerald-950/10 p-6">
        <div className="flex items-center gap-2.5 mb-1.5">
          <Sunset size={17} className="text-limed" />
          <h3 className="font-display font-bold text-emerald-950">Customer reminders</h3>
        </div>
        <p className="text-sm text-emerald-900/60 mb-5">
          Customers with a confirmed job get an automatic reminder email. Choose when it goes out.
        </p>
        <div className="grid sm:grid-cols-2 gap-3">
          {TIMING_OPTIONS.map((opt) => {
            const active = timing === opt.id;
            return (
              <button
                key={opt.id}
                data-testid={`reminder-timing-${opt.id === "evening_before" ? "evening" : "morning"}`}
                onClick={() => saveTiming(opt.id)}
                className={`text-left rounded-2xl border-2 p-4 transition-colors duration-200 ${
                  active
                    ? "border-limed bg-limed-light/60"
                    : "border-emerald-950/10 bg-sage/40 hover:border-limed/50"
                }`}
              >
                <div className="flex items-center justify-between">
                  <opt.icon size={20} className={active ? "text-pine" : "text-emerald-900/40"} />
                  {active && (
                    <span className="w-5 h-5 rounded-full bg-limed flex items-center justify-center text-pine-dark">
                      <Check size={12} strokeWidth={3} />
                    </span>
                  )}
                </div>
                <p className="mt-2 font-semibold text-emerald-950 text-sm">{opt.title}</p>
                <p className="text-xs font-mono uppercase tracking-widest text-emerald-900/50 mt-1">
                  {opt.hint}
                </p>
              </button>
            );
          })}
        </div>
      </div>

      <div data-testid="admin-availability-panel" className="bg-white rounded-3xl border border-emerald-950/10 p-6">
        <div className="flex items-center gap-2.5 mb-1.5">
          <Clock size={17} className="text-limed" />
          <h3 className="font-display font-bold text-emerald-950">Weekly working hours</h3>
        </div>
        <p className="text-sm text-emerald-900/60 mb-6">
          Tap times to turn them on or off for each day. Turned-off times show as unavailable on the
          booking calendar every week. Use Blocked Slots for one-off exceptions.
        </p>

        <div className="space-y-3 overflow-x-auto">
          {WEEKDAYS.map((day, wd) => {
            const key = String(wd);
            const enabled = settings[key] || [];
            return (
              <div key={day} className="flex items-center gap-3 min-w-[900px]">
                <p className="w-24 shrink-0 text-sm font-semibold text-emerald-950">{day}</p>
                <div className="flex gap-1.5">
                  {SLOTS.map((slot) => {
                    const on = enabled.includes(slot);
                    return (
                      <button
                        key={slot}
                        data-testid={`avail-toggle-${wd}-${slot}`}
                        onClick={() => toggle(key, slot)}
                        className={`px-2.5 py-2 rounded-xl text-xs font-semibold transition-colors duration-200 ${
                          on
                            ? "bg-limed-light text-pine border border-limed/40"
                            : "bg-slate-100 text-slate-400 line-through border border-transparent"
                        }`}
                      >
                        {slotLabel(slot)}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        <button
          data-testid="avail-save-btn"
          onClick={save}
          disabled={saving}
          className="mt-6 inline-flex items-center gap-2 bg-pine text-bone font-semibold px-6 py-3 rounded-full hover:bg-limed hover:text-pine-dark transition-colors disabled:opacity-60"
        >
          {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
          Save Availability
        </button>
      </div>
    </div>
  );
}
